import {useState} from 'react';
import {Button, Card, Form} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {DmzEntry} from '../../Types/routerForms';
import {useSaveDmz} from '../../Hooks/RouterManager';
import Level3Title from '../../../../app/common/components/title/Level3Title';

interface DmzSectionProps {
  routerId: string;
  dmz: DmzEntry[];
  publicIps: string[];
  wanIps: string[];
  privateNetworks: string[];
}

interface DmzRow {
  publicIp: string;
  network: string;
  privateLastOctet: string;
  redirectIcmp: boolean;
}

const DmzSection = ({routerId, dmz, publicIps, wanIps, privateNetworks}: DmzSectionProps): JSX.Element => {
  const intl = useIntl();
  const saveDmz = useSaveDmz(routerId);
  const defaultNetwork = privateNetworks[0] ?? '';

  const [rows, setRows] = useState<DmzRow[]>(() =>
    publicIps.map((ip) => {
      const existing = dmz.find((e) => e.publicIp === ip);
      return {
        publicIp: ip,
        network: existing?.privateNetwork || defaultNetwork,
        privateLastOctet: existing?.privateLastOctet ?? '',
        redirectIcmp: existing?.redirectIcmp ?? false,
      };
    })
  );

  const updateRow = (publicIp: string, patch: Partial<DmzRow>) => {
    setRows((prev) => prev.map((r) => (r.publicIp === publicIp ? {...r, ...patch} : r)));
  };

  const handleSave = () =>
    saveDmz.mutate(
      rows.map((r) => ({
        publicIp: r.publicIp,
        network: r.network,
        lastOctet: r.privateLastOctet,
        redirectIcmp: r.redirectIcmp,
      }))
    );

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title>
          <Level3Title
            title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.DMZ'})}
          />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="text-muted">
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.DMZ_HELP'})}
        </p>
        {rows.map((entry) => (
          <div key={entry.publicIp} className="row align-items-center g-3 mb-3">
            <div className="col-md-3">
              <strong>{entry.publicIp}</strong>{' '}
              <span className="text-muted">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.DMZ_TOWARDS'})}
              </span>
            </div>
            <div className="col-md-3">
              <Form.Select
                value={entry.network}
                onChange={(e) => updateRow(entry.publicIp, {network: e.target.value})}
              >
                {privateNetworks.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Control
                type="number"
                value={entry.privateLastOctet}
                onChange={(e) => updateRow(entry.publicIp, {privateLastOctet: e.target.value})}
                placeholder="100"
                min={0}
                max={255}
              />
            </div>
            {!wanIps.includes(entry.publicIp) && (
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.DMZ_ICMP'})}
                  checked={entry.redirectIcmp}
                  onChange={(e) => updateRow(entry.publicIp, {redirectIcmp: e.target.checked})}
                />
              </div>
            )}
          </div>
        ))}
        <Button variant="primary" onClick={handleSave} disabled={saveDmz.isLoading}>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.DMZ_SAVE'})}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default DmzSection;
